import { useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import useRouter from "common/hooks/use-router";
import useItem from "common/hooks/useItem";
import useList from "common/hooks/useList";
import useAuth from "common/hooks/useAuth";
import usePark from "common/hooks/usePark";
import AlertsContext from "common/providers/alerts";

import DetailHeader from "common/components/DetailHeader/DetailHeader";
import HeaderButtons from "common/components/DetailHeader/HeaderButtons";
import SectionTitle from "common/components/SectionTitle/SectionTitle";
import Pagination from "common/components/Table/Pagination";
import ImageComponent from "common/components/ImageComponent/ImageComponent";
import ImageFromStrapiMedia from "common/components/ImageFromStrapiMedia/ImageFromStrapiMedia";
import Icon from "components/Icon/Icon";

import getAnimalLabel from "utils/animalLabel";
import { Animal } from "types/Animal";
import { Enclosure } from "types/Enclosure";
import { Tag } from "types/Tag";
import { ObservationGroup } from "types/ObservationGroup";
import { ObservationGrid } from "types/ObservationGrid";
import ObservationResponseItem from "pages/ObservationResponses/ObservationResponseItem";

const EMPTY = "—";

function InfoRow({ label, value }: { label: string; value?: any }) {
  const hasValue = value !== null && value !== undefined && value !== "";
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
        {label}
      </span>
      <span
        className={
          hasValue ? "text-slate-700 font-medium break-words" : "text-slate-300"
        }
      >
        {hasValue ? value : EMPTY}
      </span>
    </div>
  );
}

function TextBlock({ label, value }: { label: string; value?: any }) {
  const hasValue = value !== null && value !== undefined && value !== "";
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
        {label}
      </span>
      {hasValue ? (
        <p className="text-slate-700 whitespace-pre-line leading-relaxed">
          {value}
        </p>
      ) : (
        <span className="text-slate-300">{EMPTY}</span>
      )}
    </div>
  );
}

function ChipGroup({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
        {label}
      </span>
      {items.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {items.map((item, index) => (
            <span
              // eslint-disable-next-line react/no-array-index-key
              key={`${item}-${index}`}
              className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200 text-sm"
            >
              {item}
            </span>
          ))}
        </div>
      ) : (
        <span className="text-slate-300">{EMPTY}</span>
      )}
    </div>
  );
}

function calculateAge(birthDate: string | number | Date) {
  const today = new Date();
  const date = new Date(birthDate);
  const diffTime = Math.abs(today.getTime() - date.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const years = Math.floor(diffDays / 365);
  const months = Math.floor((diffDays % 365) / 30);
  const days = (diffDays % 365) % 30;
  return { years, months, days };
}

function AnimalDetail() {
  const { t } = useTranslation();
  const router = useRouter();
  const { navigate } = router;
  const { setAlert } = useContext(AlertsContext);
  const { user: currentUser } = useAuth();
  const { park } = usePark();

  const id = router.query.id as string;

  const { item, error, isFetching }: any = useItem("animals", id, {});
  const animal = item as (Animal & { lastObservationDate?: any }) | null;

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  // Les tags renvoyés par findOne ne contiennent que leur id : on récupère les
  // noms côté client (comme TagSelect), sans modifier l'API.
  const { items: parkTags } = useList<Tag>("tags", {
    defaultFilters: park?.id ? { park: park.id } : {},
    defaultItemsPerPage: 500,
    cachePrefix: "AnimalDetail_parkTags",
  });

  const tagNames = useMemo(() => {
    const byId = new Map((parkTags ?? []).map((tag) => [tag.id, tag.name]));
    return (animal?.tags ?? [])
      .map((tag: Tag) => byId.get(tag.id) ?? tag.name)
      .filter(Boolean) as string[];
  }, [animal?.tags, parkTags]);

  const canEdit = useMemo(() => {
    if (!animal) return false;
    const samePark = Boolean(currentUser?.park?.id === animal?.park?.id);
    return (
      (currentUser?.isSuperadmin ||
        (currentUser?.isAdmin && samePark) ||
        ((currentUser?.isManager || currentUser?.isObserver) &&
          samePark &&
          currentUser?.administrationPermissions?.animal?.update)) ??
      false
    );
  }, [animal, currentUser]);

  // Grilles d'évaluation de l'animal (cache par animal : filtre correct +
  // restauration de la pagination au retour depuis une grille).
  const {
    items: grids,
    isFetching: isFetchingGrids,
    pageCount,
    setPageIndex,
    pageIndex,
    itemsCount,
    itemsPerPage,
    error: gridsError,
  } = useList<ObservationGrid>("observation-responses/data/list", {
    defaultFilters: { animal: id },
    cachePrefix: `AnimalDetail_grids_${id}`,
  });

  useEffect(() => {
    if (gridsError) {
      setAlert(gridsError, "danger");
    }
  }, [gridsError, setAlert]);

  const handleNavigateGrid = (gridId: any) => {
    navigate(`/observation-responses/${gridId}`);
  };

  if (isFetching || !animal) {
    return null;
  }

  const { species } = animal;
  const label = getAnimalLabel(
    animal.id,
    animal.houseName,
    animal.zimsId,
    animal.localId,
    animal.tag
  );

  const birthDateValue = animal.birthDate ?? animal.estimatedBirthDate;
  const isEstimated = !animal.birthDate && Boolean(animal.estimatedBirthDate);
  const age = birthDateValue ? calculateAge(birthDateValue) : null;

  const enclosureNames = (animal.enclosures ?? [])
    .map((e: Enclosure) => e?.name)
    .filter(Boolean) as string[];
  const observationGroupNames = (animal.observationGroups ?? [])
    .map((og: ObservationGroup) => og?.name)
    .filter(Boolean) as string[];

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
      <DetailHeader
        title={label}
        backLink="/animals"
        TitleIcon={
          <div className="flex items-center justify-center w-16 h-16 rounded-lg border border-primary">
            <Icon
              name={animal.isGroup ? "BsStarHalf" : "IoPawOutline"}
              className="text-primary w-7 h-7"
            />
          </div>
        }
        SubTitle={
          species ? (
            <span className="flex items-center gap-1">
              {species.scientificName && (
                <span className="font-bold italic">
                  {species.scientificName}
                </span>
              )}
              {species.name && (
                <span className="text-gray-500">{`(${species.name})`}</span>
              )}
            </span>
          ) : undefined
        }
        HeaderButtons={
          <HeaderButtons
            editUrl={`/animals/edit/${animal.id}`}
            editDisabled={!canEdit}
          />
        }
      />

      <div className="responsive-flex-lg gap-4 mt-4">
        {/* Colonne principale */}
        <div className="lg:w-2/3 flex flex-col gap-4">
          {/* Carte identité */}
          <div className="white-box">
            <div className="responsive-flex gap-6">
              <div className="w-40 h-40 shrink-0 rounded-lg overflow-hidden border border-slate-200">
                <ImageComponent
                  image={
                    animal.picture
                      ? ImageFromStrapiMedia(animal.picture)?.uri
                      : undefined
                  }
                />
              </div>
              <div className="flex-1 flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-2xl font-bold text-primary leading-tight">
                    {animal.houseName || label}
                  </h2>
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                      animal.isGroup
                        ? "bg-amber-100 text-amber-700 border-amber-400"
                        : "bg-green-100 text-green-800 border-green-500"
                    }`}
                  >
                    {animal.isGroup
                      ? t("common.individualsGroup")
                      : t("common.animal")}
                  </span>
                  {!animal.isGroup && animal.gender && (
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-500">
                      {t(`animals.${animal.gender}`)}
                    </span>
                  )}
                  {animal.isArchived && (
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500 border border-slate-300">
                      {t("forms.isArchived")}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mt-1">
                  {animal.isGroup ? (
                    <>
                      <InfoRow
                        label={t("forms.individualsNumber")}
                        value={animal.individualsNumber}
                      />
                      <InfoRow
                        label={t("animals.nbMale")}
                        value={(animal as any).nbMaleInGroup}
                      />
                      <InfoRow
                        label={t("animals.nbFemale")}
                        value={(animal as any).nbFemaleInGroup}
                      />
                      <InfoRow
                        label={t("animals.nbUndefinedGender")}
                        value={(animal as any).nbUndefinedGenderInGroup}
                      />
                    </>
                  ) : (
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                        {isEstimated
                          ? t("forms.estimatedBirthDate")
                          : t("forms.birthDate")}
                      </span>
                      {birthDateValue ? (
                        <>
                          <span className="text-slate-700 font-medium">
                            {dayjs(birthDateValue).format("DD/MM/YYYY")}
                          </span>
                          {age && (
                            <span className="text-sm text-slate-400">
                              {t(
                                isEstimated
                                  ? "animals.estimatedAgeIs"
                                  : "animals.ageIs",
                                {
                                  years: age.years,
                                  months: age.months,
                                  days: age.days,
                                }
                              )}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-slate-300">{EMPTY}</span>
                      )}
                    </div>
                  )}
                  <InfoRow
                    label={t("animals.reproductiveStatus")}
                    value={
                      animal.reproductiveStatus
                        ? t(`animals.${animal.reproductiveStatus}`)
                        : null
                    }
                  />
                </div>

                <p className="mt-2 text-sm text-slate-500">
                  {animal.lastObservationDate
                    ? t("animals.lastObservationDate", {
                        date: dayjs(animal.lastObservationDate).fromNow(),
                      })
                    : t("animals.notObservedYet")}
                </p>
              </div>
            </div>
          </div>

          {/* Identifiants */}
          <div className="white-box">
            <SectionTitle title={t("forms.identifiers")} className="mb-4" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <InfoRow
                label={
                  park?.hasZimsImport
                    ? t("animals.localId")
                    : t("animals.local")
                }
                value={animal.localId}
              />
              <InfoRow
                label={
                  park?.hasZimsImport
                    ? t("animals.zimsId")
                    : t("animals.otherId")
                }
                value={animal.zimsId}
              />
              <InfoRow label={t("animals.band")} value={animal.band} />
              <InfoRow
                label={t("animals.transponderId")}
                value={animal.transponderId}
              />
              <div className="sm:col-span-2">
                <InfoRow label={t("animals.tag")} value={animal.tag} />
              </div>
            </div>
          </div>

          {/* Grilles d'évaluation */}
          <div className="white-box">
            <SectionTitle
              title={`${t("forms.observationGrids")} (${itemsCount})`}
              className="mb-2"
            />
            {grids && grids.length > 0 ? (
              <>
                {grids.map((grid: any) => (
                  <ObservationResponseItem
                    key={grid.id}
                    item={grid}
                    onNavigate={handleNavigateGrid}
                  />
                ))}
                {pageCount > 1 && (
                  <Pagination
                    goToPage={setPageIndex}
                    nextPage={() => setPageIndex(pageIndex + 1)}
                    previousPage={() => setPageIndex(pageIndex - 1)}
                    totalPage={pageCount}
                    disabled={isFetchingGrids}
                    pageIndex={pageIndex}
                    itemsPerPage={itemsPerPage}
                    itemsCount={itemsCount}
                    background="bg-white"
                  />
                )}
              </>
            ) : (
              !isFetchingGrids && (
                <div className="py-6 text-center text-slate-400">
                  {t("common.noResult")}
                </div>
              )
            )}
          </div>
        </div>

        {/* Colonne latérale */}
        <div className="lg:w-1/3 flex flex-col gap-4">
          <div className="white-box">
            <SectionTitle title={t("forms.park")} className="mb-4" />
            <div className="flex flex-col gap-4">
              <ChipGroup label={t("forms.enclosures")} items={enclosureNames} />
              <ChipGroup
                label={t("forms.observationGroups")}
                items={observationGroupNames}
              />
              <ChipGroup label={t("forms.tags")} items={tagNames} />
            </div>
          </div>

          <div className="white-box">
            <SectionTitle title={t("forms.food")} className="mb-4" />
            <div className="flex flex-col gap-4">
              {species?.food ? (
                <TextBlock
                  label={t("forms.speciesFood")}
                  value={species.food}
                />
              ) : null}
              <TextBlock
                label={t("forms.animalFood")}
                value={(animal as any).food}
              />
            </div>
          </div>

          <div className="white-box">
            <SectionTitle title={t("forms.medical")} className="mb-4" />
            <div className="flex flex-col gap-4">
              <TextBlock
                label={t("forms.medicalProblem")}
                value={(animal as any).medicalProblem}
              />
              <TextBlock
                label={t("forms.otherMedicalProblem")}
                value={(animal as any).otherMedicalProblem}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AnimalDetail;
